import {
  Badge,
  Box,
  Card,
  Divider,
  Grid,
  Group,
  Loader,
  RingProgress,
  Select,
  Text,
  ThemeIcon,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Activity, Calendar, FileText, X } from "tabler-icons-react";
import HeaderPage from "../../../components/HeaderPage";
import { useGetEleveQuery } from "../../../redux/features/eleveApi";
import { useGetStudentActivitiesQuery } from "../../../redux/features/eleveApi";
import {
  ACTIVITY_TYPE_OPTIONS,
  getActivityType,
} from "../utils/activityTypes";

const toApiDate = (date) => (date ? date.toISOString().split("T")[0] : undefined);

const formatDate = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Couleur de l'anneau de note en fonction du pourcentage de réussite.
const noteColor = (note, total) => {
  if (!total) return "blue";
  const ratio = (note / total) * 100;
  if (ratio >= 70) return "teal";
  if (ratio >= 40) return "orange";
  return "red";
};

function ActivityCard({ activity, onClick }) {
  const typeConfig = getActivityType(activity?.type);
  const total = activity?.total_questions ?? 0;
  const note = activity?.note ?? 0;
  const ringColor = noteColor(note, total);
  const ratio = total ? Math.min((note / total) * 100, 100) : 0;

  return (
    <Card
      withBorder
      radius="md"
      p="lg"
      shadow="sm"
      onClick={onClick}
      style={{ cursor: "pointer", height: "100%", transition: "transform .15s" }}
      sx={(theme) => ({
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: theme.shadows.md,
          borderColor: theme.colors[typeConfig.color]?.[4],
        },
      })}
    >
      <Group position="apart" noWrap align="flex-start">
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Badge color={typeConfig.color} variant="light" mb={8}>
            {typeConfig.label}
          </Badge>
          <Text weight={700} size="lg" lineClamp={2}>
            {activity?.title || "Sans titre"}
          </Text>
          <Group spacing={6} mt={10} noWrap>
            <Calendar size={15} color="gray" />
            <Text size="xs" color="dimmed">
              {formatDate(activity?.date)}
            </Text>
          </Group>
        </Box>

        <RingProgress
          size={80}
          thickness={7}
          roundCaps
          sections={[{ value: ratio, color: ringColor }]}
          label={
            <Text align="center" weight={700} size="sm">
              {note}
            </Text>
          }
        />
      </Group>

      <Divider my="sm" variant="dashed" />

      <Group position="apart">
        <Group spacing={6}>
          <FileText size={15} color="gray" />
          <Text size="xs" color="dimmed">
            {total} question{total > 1 ? "s" : ""}
          </Text>
        </Group>
        <Badge
          color={activity?.status === "COMPLETED" ? "teal" : "gray"}
          variant="dot"
          size="sm"
        >
          {activity?.status === "COMPLETED" ? "Terminé" : activity?.status}
        </Badge>
      </Group>
    </Card>
  );
}

function EleveActivitesPage() {
  const params = useParams();
  const navigate = useNavigate();
  const studentId = parseInt(params.id);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [type, setType] = useState(null);

  const { data: eleve } = useGetEleveQuery(studentId, { skip: !params.id });

  const { data, isLoading, isFetching } = useGetStudentActivitiesQuery(
    {
      studentId,
      startDate: toApiDate(startDate),
      endDate: toApiDate(endDate),
      type: type || undefined,
    },
    { skip: !params.id },
  );

  const activities = data?.data ?? [];
  const hasFilters = startDate || endDate || type;

  const resetFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setType(null);
  };

  return (
    <Box>
      <HeaderPage
        navigation={navigate}
        title={`Activités de : ${
          eleve ? `${eleve.firstName} ${eleve.lastName}` : "..."
        }`}
      />

      <Divider my={20} />

      {/* Filtres */}
      <Card withBorder radius="md" p="md" mb={20}>
        <Grid align="flex-end">
          <Grid.Col lg={3} sm={6} xs={12}>
            <Select
              label="Type d'activité"
              placeholder="Tous les types"
              data={ACTIVITY_TYPE_OPTIONS}
              value={type}
              onChange={setType}
              clearable
              withinPortal
              dropdownPosition="bottom"
              zIndex={2000}
            />
          </Grid.Col>
          <Grid.Col lg={3} sm={6} xs={12}>
            <DatePickerInput
              label="Date de début"
              placeholder="Sélectionner"
              value={startDate}
              onChange={setStartDate}
              clearable
              valueFormat="DD/MM/YYYY"
              dropdownType="popover"
              popoverProps={{ withinPortal: true, zIndex: 2000, position: "bottom" }}
            />
          </Grid.Col>
          <Grid.Col lg={3} sm={6} xs={12}>
            <DatePickerInput
              label="Date de fin"
              placeholder="Sélectionner"
              value={endDate}
              onChange={setEndDate}
              clearable
              valueFormat="DD/MM/YYYY"
              dropdownType="popover"
              popoverProps={{ withinPortal: true, zIndex: 2000, position: "bottom" }}
            />
          </Grid.Col>
          {hasFilters && (
            <Grid.Col lg={3} sm={6} xs={12}>
              <Badge
                color="gray"
                variant="light"
                size="lg"
                rightSection={<X size={12} />}
                onClick={resetFilters}
                style={{ cursor: "pointer" }}
              >
                Réinitialiser les filtres
              </Badge>
            </Grid.Col>
          )}
        </Grid>
      </Card>

      {isLoading || isFetching ? (
        <Group position="center" py={40}>
          <Loader />
        </Group>
      ) : activities.length === 0 ? (
        <Card withBorder radius="md" p="xl">
          <Group position="center" spacing="sm">
            <ThemeIcon color="gray" variant="light" size="lg" radius="xl">
              <Activity size={18} />
            </ThemeIcon>
            <Text color="dimmed">Aucune activité pour cette période</Text>
          </Group>
        </Card>
      ) : (
        <>
          <Text color="dimmed" size="sm" mb={10}>
            {activities.length} activité{activities.length > 1 ? "s" : ""}
          </Text>
          <Grid>
            {activities.map((activity) => (
              <Grid.Col key={activity.id} lg={4} md={6} xs={12}>
                <ActivityCard
                  activity={activity}
                  onClick={() =>
                    navigate(`${activity.type}/${activity.id}`)
                  }
                />
              </Grid.Col>
            ))}
          </Grid>
        </>
      )}
    </Box>
  );
}

export default EleveActivitesPage;
